
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import GalleryNodeView from './GalleryNodeView';

export interface GalleryOptions {
  HTMLAttributes: Record<string, any>;
}

export interface GalleryImage {
  url: string;
  caption?: string;
  altText?: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    gallery: {
      /**
       * Add a gallery
       */
      setGallery: (options: { 
        images: GalleryImage[];
        layout: 'grid' | 'slider' | 'masonry';
        title?: string;
      }) => ReturnType;
    };
  }
}

export const GalleryExtension = Node.create<GalleryOptions>({
  name: 'gallery',
  
  group: 'block',
  
  content: '',
  
  marks: '',
  
  selectable: true,
  
  draggable: true,
  
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'article-gallery',
      },
    };
  },

  addAttributes() {
    return {
      images: {
        default: [],
      },
      layout: {
        default: 'grid',
      },
      title: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[class="article-gallery"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { images, layout, title } = HTMLAttributes;
    
    // Create the base attributes for the main container
    const galleryAttributes = mergeAttributes(this.options.HTMLAttributes, {
      'class': `article-gallery article-gallery-${layout}`,
      'data-type': this.name,
      'data-layout': layout
    });

    // Build the gallery content based on layout
    if (layout === 'slider') {
      // For slider layout, we need to be careful with the structure
      let galleryContent = [];
      
      // Add title if present
      if (title) {
        galleryContent.push(['h4', { class: 'gallery-title' }, title]);
      }
      
      // Create the slider wrapper with nested elements
      let sliderContent = [];
      
      // Create carousel element
      const carouselItems = [];
      
      if (images && images.length > 0) {
        images.forEach((image: GalleryImage) => {
          const figureContent = [];
          
          if (image.url) {
            figureContent.push(['img', { 
              src: image.url, 
              alt: image.altText || '', 
              loading: 'lazy' 
            }]);
          } else {
            figureContent.push(['div', { class: 'gallery-image-error' }, 'Invalid image source']);
          }
          
          if (image.caption) {
            figureContent.push(['figcaption', {}, image.caption]);
          }
          
          carouselItems.push(['div', { class: 'carousel-item' }, 
            ['figure', { class: 'gallery-item' }, ...figureContent]
          ]);
        });
      }
      
      // Build the carousel structure properly
      sliderContent.push(['div', { class: 'carousel', 'data-carousel': 'true' }, 
        ['div', { class: 'carousel-content' }, ...carouselItems]
      ]);
      
      // Add navigation buttons
      sliderContent.push(['button', { 
        class: 'carousel-prev', 
        'aria-label': 'Previous slide',
        'data-carousel-prev': 'true'
      }, '←']);
      
      sliderContent.push(['button', { 
        class: 'carousel-next', 
        'aria-label': 'Next slide',
        'data-carousel-next': 'true'
      }, '→']);
      
      // Add slider wrapper to gallery content
      galleryContent.push(['div', { class: 'gallery-slider-wrapper' }, ...sliderContent]);
      
      // Return the complete gallery structure
      return ['div', galleryAttributes, ...galleryContent];
    } else {
      // For grid and masonry layouts
      let galleryContent = [];
      
      // Add title if present
      if (title) {
        galleryContent.push(['h4', { class: 'gallery-title' }, title]);
      }
      
      // Create the image container
      const imageItems = [];
      
      // Add images if present
      if (images && images.length > 0) {
        images.forEach((image: GalleryImage) => {
          const figureContent = [];
          
          // Add the image with proper attributes
          if (image.url) {
            figureContent.push(['img', { 
              src: image.url, 
              alt: image.altText || '', 
              loading: 'lazy' 
            }]);
          } else {
            figureContent.push(['div', { class: 'gallery-image-error' }, 'Invalid image source']);
          }
          
          // Add caption if present
          if (image.caption) {
            figureContent.push(['figcaption', {}, image.caption]);
          }
          
          // Create the figure element
          imageItems.push(['figure', { class: 'gallery-item' }, ...figureContent]);
        });
      }
      
      // Add the container with all images
      galleryContent.push(['div', { class: `gallery-${layout}` }, ...imageItems]);
      
      // Return with the correct format
      return ['div', galleryAttributes, ...galleryContent];
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(GalleryNodeView);
  },

  addCommands() {
    return {
      setGallery:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});

export default GalleryExtension;
